/**
 * ScenarioComparisonTile — Home-page side-by-side comparison of
 * the CLO's pinned Portfolio Analysis scenarios.
 *
 * Once a CLO has pinned 2+ scenarios (PE Motivation fix, ROE
 * variance close, LEI pillar focus, etc.) this tile surfaces them
 * as a scannable table keyed off the `stats` array we captured at
 * pin time. Winner pills highlight the best ROI and biggest net
 * benefit so the steering-committee conversation starts with the
 * highest-leverage move instead of a flat list.
 *
 * Rendered nothing when fewer than 2 portfolio-analysis pins exist
 * — the tile is a comparison surface, not a single-scenario
 * display (the Quarterly Deck already shows the detail).
 *
 * Data: GET /api/quarterly-deck/pins  (filters client-side to
 *       theme="portfolio-analysis").
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Layers, ExternalLink, Trophy, Medal, Pin } from 'lucide-react';
import api from '../../lib/api';
import logger from '../../lib/logger';

const ENGINE_BADGE = {
  pe: { label: 'PE', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
  roe: { label: 'ROE', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
  roi: { label: 'ROI', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  lei: { label: 'LEI', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
  maturity: { label: 'Maturity', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  portfolio: { label: 'Ad-hoc', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

// Extract a labelled stat value from the pin's `stats` array.
// Stats are free-text strings (e.g. "3.2x" / "$1.4M" / "9.3 mo")
// but for comparison we need numerics — parseStatNumeric strips
// the label decoration and returns a float for sort, plus the raw
// display string for render.
function parseStatNumeric(statValue) {
  if (!statValue) return { num: null, raw: '' };
  const raw = String(statValue).trim();
  const num = parseFloat(raw.replace(/[^0-9.-]/g, ''));
  const multiplier = /m\b/i.test(raw) ? 1_000_000 : /k\b/i.test(raw) ? 1_000 : /b\b/i.test(raw) ? 1_000_000_000 : 1;
  return { num: isFinite(num) ? num * multiplier : null, raw };
}

function findStat(pin, label) {
  const row = (pin.stats || []).find((s) => s.label === label);
  return row ? parseStatNumeric(row.value) : { num: null, raw: '—' };
}

export function ScenarioComparisonTile() {
  const [scenarios, setScenarios] = useState(null); // null=loading, []=none
  const [sortMode, setSortMode] = useState('roi'); // 'roi' | 'benefit' | 'origin'
  const load = useCallback(async () => {
    try {
      const res = await api.get('/quarterly-deck/pins?limit=100');
      const all = res?.data?.pins || res?.data || [];
      const portfolio = (Array.isArray(all) ? all : [])
        .filter((p) => p?.theme === 'portfolio-analysis')
        .slice(0, 6); // cap at 6 so the table never gets unwieldy
      setScenarios(portfolio);
    } catch (err) {
      logger.error?.('scenario comparison load failed:', err);
      setScenarios([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Silent when under 2 scenarios — comparison surface, not a single-row display.
  if (!scenarios || scenarios.length < 2) return null;

  // Identify winners for the callout pills. Tie-break by higher
  // ROI (so "2 measures @ 3.1x" beats "2 measures @ 3.1x" only
  // when its ROI is genuinely higher — deterministic by pin id).
  const scored = scenarios.map((p) => ({
    ...p,
    roi: findStat(p, 'ROI'),
    netBenefit: findStat(p, 'Net benefit'),
    kpi: findStat(p, 'KPI uplift'),
    payback: findStat(p, 'Payback'),
  }));
  const bestROI = scored.reduce(
    (best, s) => ((s.roi.num ?? -Infinity) > (best.roi.num ?? -Infinity) ? s : best),
    scored[0],
  );
  const bestBenefit = scored.reduce(
    (best, s) => ((s.netBenefit.num ?? -Infinity) > (best.netBenefit.num ?? -Infinity) ? s : best),
    scored[0],
  );

  // Apply the active sort mode. `roi` and `benefit` are plain
  // descending numeric sorts (nulls sink to the bottom). `origin`
  // clusters by engine first (alphabetical), then by ROI within
  // each cluster — so multiple PE fixes appear together, sorted
  // strongest-first within the PE group.
  const sorted = [...scored].sort((a, b) => {
    if (sortMode === 'benefit') {
      return (b.netBenefit.num ?? -Infinity) - (a.netBenefit.num ?? -Infinity);
    }
    if (sortMode === 'origin') {
      const ao = a.origin_engine || 'zzzz';
      const bo = b.origin_engine || 'zzzz';
      if (ao !== bo) return ao.localeCompare(bo);
      return (b.roi.num ?? -Infinity) - (a.roi.num ?? -Infinity);
    }
    return (b.roi.num ?? -Infinity) - (a.roi.num ?? -Infinity);
  });

  return (
    <Card
      className="border-teal-200 bg-gradient-to-br from-white to-teal-50/40"
      data-testid="scenario-comparison-tile"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Layers className="h-4 w-4 text-teal-600" />
              Pinned scenarios — side-by-side
            </CardTitle>
            <CardDescription className="mt-0.5">
              Your {scenarios.length} pinned Portfolio Analysis scenarios, ranked on ROI and net benefit. Start your steering-committee conversation with the highest-leverage move.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div
              role="group"
              aria-label="Sort mode"
              className="inline-flex bg-white border border-slate-200 rounded-md p-0.5"
              data-testid="scenario-comparison-sort-control"
            >
              {[
                { id: 'roi', label: 'By ROI' },
                { id: 'benefit', label: 'By $' },
                { id: 'origin', label: 'By origin' },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setSortMode(o.id)}
                  aria-pressed={sortMode === o.id}
                  className={
                    'px-2 py-0.5 text-[11px] font-medium rounded transition-colors ' +
                    (sortMode === o.id
                      ? 'bg-teal-600 text-white'
                      : 'text-slate-600 hover:bg-slate-100')
                  }
                  data-testid={`scenario-comparison-sort-${o.id}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <Link to="/portfolio">
              <Button variant="outline" size="sm" data-testid="scenario-comparison-model-new">
                <Pin className="h-3.5 w-3.5 mr-1" />
                Model another scenario
              </Button>
            </Link>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="scenario-comparison-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-teal-100">
                <th className="text-left py-2 pr-3 font-semibold">Scenario</th>
                <th className="text-right py-2 px-3 font-semibold">ROI</th>
                <th className="text-right py-2 px-3 font-semibold">Net benefit</th>
                <th className="text-right py-2 px-3 font-semibold">KPI uplift</th>
                <th className="text-right py-2 pl-3 font-semibold">Payback</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-teal-50 last:border-0 hover:bg-white/60"
                  data-testid={`scenario-comparison-row-${s.id}`}
                >
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {s.origin_engine && ENGINE_BADGE[s.origin_engine] && (
                        <span
                          className={`inline-flex items-center rounded-full border px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wider ${ENGINE_BADGE[s.origin_engine].cls}`}
                          title={s.origin_label ? `Scoped to close: ${s.origin_label}` : `Origin: ${s.origin_engine}`}
                          data-testid={`scenario-origin-${s.origin_engine}`}
                        >
                          {ENGINE_BADGE[s.origin_engine].label}
                        </span>
                      )}
                      <Link
                        to={s.path || '/portfolio'}
                        className="font-medium text-slate-800 hover:text-teal-700 hover:underline flex items-center gap-1"
                      >
                        {(s.title || 'Portfolio scenario').replace('Portfolio scenario: ', '')}
                        <ExternalLink className="h-3 w-3 opacity-60" />
                      </Link>
                      {s.id === bestROI.id && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wider"
                          data-testid="scenario-best-roi-pill"
                        >
                          <Trophy className="h-2.5 w-2.5" /> best ROI
                        </span>
                      )}
                      {s.id === bestBenefit.id && s.id !== bestROI.id && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded-full bg-teal-100 text-teal-800 border border-teal-300 px-1.5 py-[1px] text-[9px] font-semibold uppercase tracking-wider"
                          data-testid="scenario-best-benefit-pill"
                        >
                          <Medal className="h-2.5 w-2.5" /> biggest $
                        </span>
                      )}
                    </div>
                    {s.description && (
                      <p className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[24rem]">{s.description}</p>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums font-semibold text-slate-800">{s.roi.raw}</td>
                  <td className="py-2 px-3 text-right tabular-nums font-semibold text-slate-800">{s.netBenefit.raw}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-slate-700">{s.kpi.raw}</td>
                  <td className="py-2 pl-3 text-right tabular-nums text-slate-700">{s.payback.raw}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
